import { type SyntheticEvent, useCallback } from 'react';

import { memoWithDisplayName } from '@/packages/react-utils/memoWithDisplayName';
import { useScrollToTopWithCallback } from '@/packages/react-utils/useScrollToTopWithCallback';
import { boundActionsFactory } from '@/packages/redux-state/store';
import { usePageSelector } from '@/packages/redux-state/store/slices/jsonQuery';
import { extractUndefined } from '@/packages/utils';

import { useListingContext } from '../../context/ListingContext';
import { type PaginationItem } from '../../types/pagination';

import { Pagination } from './components/Pagination';
import { usePagination } from './hooks/usePagination';

type TPaginationContainerProps = {
  onPageClick(): void;
};

const useBoundActions = boundActionsFactory(reduxActions => ({
  setPage: reduxActions.jsonQuery.setPageWithUpdateQueryKey,
}));

export const PaginationContainer = memoWithDisplayName<TPaginationContainerProps>(({ onPageClick }) => {
  const {
    listingProperties: { offersPerPage, aggregatedOffersCount: totalOffers, maxPage },
  } = useListingContext();

  const page = usePageSelector();

  const scrollToTopWithCallback = useScrollToTopWithCallback();

  const { pages, nextPage, prevPage } = usePagination({
    maxPage: extractUndefined(maxPage),
    offersPerPage,
    page,
    totalOffers,
  });

  const boundActions = useBoundActions();

  const handlePageClick = useCallback(
    async (_: SyntheticEvent<HTMLButtonElement>, paginationItem: PaginationItem) => {
      scrollToTopWithCallback(() => {
        boundActions.setPage(paginationItem.page);

        onPageClick();
      });
    },
    [boundActions, onPageClick, scrollToTopWithCallback],
  );

  if (!pages.length) {
    return null;
  }

  return <Pagination nextButton={nextPage} pages={pages} prevButton={prevPage} onClick={handlePageClick} />;
}, 'PaginationController');
